import { Check } from "lucide-react-native";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "../../../../../ui/avatar";
import { Badge, BadgeText } from "../../../../../ui/badge";
import { Box } from "../../../../../ui/box";
import { HStack } from "../../../../../ui/hstack";
import { Icon } from "../../../../../ui/icon";
import { Pressable } from "../../../../../ui/pressable";
import { Text } from "../../../../../ui/text";
import { VStack } from "../../../../../ui/vstack";
import type { SelectOption } from "../../../wire";

type Props = {
  option: SelectOption;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
};

const cardTone = (
  selected: boolean,
  disabled: boolean,
  pressed: boolean,
): string =>
  `${
    selected
      ? "border-brand-400 bg-brand-50"
      : // `hover:` compiles to a real CSS :hover on web, so a mouse user gets a cue
        // before committing to a click — `active:` would not, since RN Pressable
        // press state only ever reaches the DOM through the `pressed` render prop.
        "border-outline-100 bg-background-0 hover:border-outline-200 hover:bg-background-50"
  } ${
    // A background tint for the press, NOT opacity. Dimming a card that has a
    // product photo in it looks like the image failed to load.
    pressed && !disabled && !selected ? "bg-background-50" : ""
  } ${disabled ? "opacity-40" : ""}`;

/**
 * One choice, as a row.
 *
 * Carries an image and a badge so a product is recognizable as *their*
 * product (its photo, its price) rather than a row of ids they have to trust.
 *
 * `Pressable` rather than `Button` on purpose: this is a clickable card with rich
 * children, which is the case the styling rules carve out for it.
 */
const OptionCard = ({ option, selected, disabled, onPress }: Props) => (
  <Pressable onPress={onPress} disabled={disabled} aria-selected={selected}>
    {({ pressed }) => (
      <HStack
        space="sm"
        className={`items-center rounded-xl border px-3 py-2.5 ${cardTone(selected, disabled, pressed)}`}
      >
        {option.imageUrl ? (
          // md, not sm: at thirty rows of real product photos, a small avatar reads as
          // an icon rather than as a picture of the thing you are choosing.
          <Avatar size="md">
            <AvatarFallbackText>{option.label}</AvatarFallbackText>
            <AvatarImage source={{ uri: option.imageUrl }} />
          </Avatar>
        ) : null}

        <VStack className="flex-1">
          <Text
            className={`text-sm font-medium ${
              selected ? "text-brand-700" : "text-typography-800"
            }`}
          >
            {option.label}
          </Text>
          {option.description ? (
            <Text numberOfLines={2} className="text-xs text-typography-500">
              {option.description}
            </Text>
          ) : null}
        </VStack>

        {/* A bounded badge rather than loose text: on a dense list the eye can jump
              straight down the price column without re-reading every name. */}
        {option.badge ? (
          <Badge action="muted" variant="outline" size="sm" rounded="md">
            <BadgeText>{option.badge}</BadgeText>
          </Badge>
        ) : null}

        {/* A fixed-width slot, occupied or not, so ticking a box does not shuffle
              the row's layout underneath the cursor. */}
        <Box className="w-5 items-center">
          {selected ? (
            <Icon as={Check} size="sm" className="text-brand-600" />
          ) : null}
        </Box>
      </HStack>
    )}
  </Pressable>
);

export default OptionCard;
